<script setup lang="ts">
import { computed, ref } from 'vue'
import { useFocus } from '@vueuse/core'

const canSet = (value: unknown) => {
  const stringNumber = value?.toString?.() ?? ''
  if (stringNumber === '.') return false
  return true
}

const props = withDefaults(
  defineProps<{
    modelValue?: string | number
    description?: string
    disabled?: boolean
    error?: string
    id?: string
    inline?: boolean
    inputClass?: string
    label?: string
    name?: string
    onChange?: any
    onInput?: any
    placeholder?: string
    property?: string
    required?: boolean
    isPrecalculated?: boolean
    v$?: any
    useActionsSlot?: boolean
    disabledVariant?: string
  }>(),
  {
    disabled: false,
    inline: false,
    placeholder: '',
    useActionsSlot: false,
    isPrecalculated: false,
    disabledVariant: 'default',
    v$: {},
    onChange: () => {},
    onInput: () => {},
  },
)

const emit = defineEmits(['update:modelValue'])
const inputRef = ref<HTMLInputElement | null>(null)
const { focused: isFocused } = useFocus(inputRef)

const toDecimal = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '') return null
  const cleaned = raw.toString().replace(/[^0-9.-]+/g, '')
  if (cleaned === '' || cleaned === '.') return null
  const numeric = Number(cleaned)
  if (Number.isNaN(numeric)) return null
  return numeric
}

const formatDisplayPercent = (decimalValue: number) => {
  const percentValue = props.isPrecalculated ? decimalValue : decimalValue * 100
  return `${percentValue.toLocaleString()}%`
}

const value = computed({
  get: () => {
    const decimalValue = toDecimal(props.modelValue)

    if (isFocused.value) {
      if (decimalValue === null) return props.modelValue
      return (props.isPrecalculated ? decimalValue : decimalValue * 100).toString()
    }

    if (decimalValue === null) return props.modelValue
    return formatDisplayPercent(decimalValue)
  },
  set: (value) => {
    if (!canSet(value)) return

    const cleanedValue = value?.toString().replace(/[^0-9.-]+/g, '')
    if (cleanedValue === '' || cleanedValue === undefined) {
      emit('update:modelValue', null)
      return
    }

    const numericPercent = Number(cleanedValue)
    if (Number.isNaN(numericPercent)) {
      emit('update:modelValue', null)
      return
    }

    emit('update:modelValue', props.isPrecalculated ? numericPercent : numericPercent / 100)
  },
})

const error = computed(() => props.error || props.v$[props.property || props.name]?.$errors[0]?.$message)

const inputType = computed(() => 'text')

const onInput = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (!canSet(target.value)) return
  if (!props.onInput) return
  props.onInput()
}

const disabledVariants = {
  default: 'bg-gray-50 text-gray-500',
  light: 'bg-gray-50/50 text-gray-700',
  text: '!border-0 !rounded-none !shadow-none font-semibold !p-0',
}

const disabledClass = computed(() => {
  return props.disabled ? disabledVariants[props.disabledVariant] : ''
})
</script>

<template>
  <fieldset :id="id || property">
    <label :for="id" class="block text-sm font-medium text-gray-700">
      <span>{{ label }}</span>
      <span v-if="required && disabledVariant !== 'text'">*</span>
    </label>
    <div :class="{ 'mt-1': !inline && label, relative: useActionsSlot }">
      <input
        v-model="value"
        class="block w-full"
        :class="[
          'block w-full',
          error
            ? 'border-red-300 focus:border-red-300 focus:ring-red-200'
            : 'border-gray-300 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50',
          inline ? 'border-none' : 'mt-1 rounded-md border',
          inputClass,
          disabledClass,
        ]"
        :disabled="disabled"
        :id="id"
        :name="name || property"
        :placeholder="placeholder"
        :type="inputType"
        @change="onChange"
        @input="onInput"
        ref="inputRef"
      />
      <div class="absolute inset-y-0 left-0 pl-1.5" v-if="useActionsSlot">
        <slot name="actions"></slot>
      </div>
    </div>
    <template v-if="error && !inline">
      <p class="mt-1 text-sm text-red-500">{{ error }}</p>
    </template>
    <template v-if="description">
      <p class="mt-1 text-sm text-gray-500" :id="`${id}-description`">{{ description }}</p>
    </template>
  </fieldset>
</template>
